import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
} from "@mui/material";
import { AuthorizationBatch, AuthorizationJob } from "./authorization-types";

// Extended type to include garment_type which may not be in the base type
type ExtendedAuthorizationJob = AuthorizationJob & {
  garment_type?: string;
};
import {
  User,
  JobStatus,
  SheetData,
  statusToLabel,
} from "../../pages/playground/types";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  deleteJobAuthorization,
  getJobsForAuthorization,
} from "../../api/authorization";
import { Search, ArrowBack, PersonAdd, Delete } from "@mui/icons-material";
import { JobAssignmentDialog } from "./job-assignment-dialog";
import { JobAuthorizationBatchItem } from "./job-authorization-batch-item";
import { getSheetData } from "../../api/sheet";

export default function JobAuthorizationBatch({
  batch,
  copywriters,
  onClose,
}: {
  batch: AuthorizationBatch;
  copywriters: User[];
  onClose: () => void;
}) {
  // State for job data and UI controls
  const [batchJobs, setBatchJobs] = useState<ExtendedAuthorizationJob[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [assignmentDialogOpen, setAssignmentDialogOpen] =
    useState<boolean>(false);
  const [singleJobAssignmentId, setSingleJobAssignmentId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showUnassignedOnly, setShowUnassignedOnly] = useState<boolean>(false);

  const [sheet, setSheet] = useState<SheetData>();
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);

  // Special columns that come directly from the job object, not the sheet data
  // Use useMemo to avoid recreating the object on every render
  const SPECIAL_COLUMNS = useMemo(
    () => ({
      GARMENT_TYPE: "garment_type",
    }),
    []
  );

  // Load job data using useCallback to properly handle dependencies
  const fetchBatchJobs = useCallback(async () => {
    try {
      const response = await getJobsForAuthorization(batch.id);
      setBatchJobs(response.data.data);

      const sheet = await getSheetData(batch.id);
      const sheetData = sheet.data.data;
      setSheet(sheetData);

      // Extract available columns from the first item if available
      if (sheetData && sheetData.items.length > 0) {
        const columns = Object.keys(sheetData.items[0]).filter(
          (key) =>
            // Filter out any functions or non-data properties
            typeof sheetData.items[0][key] !== "function" && key !== "__proto__"
        );

        // Add special columns from job properties
        const allColumns = [SPECIAL_COLUMNS.GARMENT_TYPE, ...columns];
        setAvailableColumns(allColumns);
      }
    } catch (error) {
      console.error("Error fetching batch jobs:", error);
    }
  }, [batch.id, SPECIAL_COLUMNS.GARMENT_TYPE]);

  useEffect(() => {
    fetchBatchJobs();
  }, [fetchBatchJobs]);

  // Apply filters to jobs and group if needed
  const filteredAndGroupedJobs = useMemo(() => {
    // First filter jobs based on search, status, and assignment filters
    const filtered = batchJobs.filter((job) => {
      // Apply search filter
      const matchesSearch = job.sku
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      // Apply status filter
      const matchesStatus =
        statusFilter === "all" || job.status === statusFilter;

      // Apply assignment filter
      const matchesAssignment = !showUnassignedOnly || !job.is_assigned;

      return matchesSearch && matchesStatus && matchesAssignment;
    });

    // If no column is selected, just return the filtered jobs
    if (
      !selectedColumn ||
      (!sheet && !Object.values(SPECIAL_COLUMNS).includes(selectedColumn))
    ) {
      return {
        groups: null,
        jobs: filtered,
      };
    }

    // Group the jobs by the selected column
    const groups: Record<string, ExtendedAuthorizationJob[]> = {};

    filtered.forEach((job) => {
      let groupKey = "Not specified";

      // Handle special columns that come directly from the job object
      if (selectedColumn === SPECIAL_COLUMNS.GARMENT_TYPE) {
        // Use job's garment_type property
        groupKey = job.garment_type || "Not specified";
      }
      // Handle sheet data columns
      else if (sheet && sheet.items.length > 0) {
        // Since the SKU is always the first field, use that for matching
        const sheetItems = sheet.items.filter((item) => {
          // Get the first property (which is the SKU field, whatever it's called)
          const skuField = Object.keys(item)[0];
          // String coercion: pandas parses numeric-only SKU columns as floats,
          // but job.sku is always a string. Compare as strings to match.
          return String(item[skuField]).trim() === String(job.sku).trim();
        });

        if (sheetItems && sheetItems.length > 0) {
          // Use the first sheet item's value for the selected column as the group key
          if (selectedColumn in sheetItems[0]) {
            groupKey =
              sheetItems[0][selectedColumn]?.toString() || "Not specified";
          } else {
            // Column not found in the item
            groupKey = "Not specified";
          }
        }
      }

      // Add job to the appropriate group
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(job);
    });

    return {
      groups,
      jobs: filtered,
    };
  }, [
    batchJobs,
    searchQuery,
    statusFilter,
    showUnassignedOnly,
    selectedColumn,
    sheet,
    SPECIAL_COLUMNS, // Added SPECIAL_COLUMNS as a dependency
  ]);

  // Get the final list of jobs to display
  const filteredJobs =
    filteredAndGroupedJobs.jobs as ExtendedAuthorizationJob[];

  // Handle job selection
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredJobs.map((job) => job.id);
      setSelectedJobs(newSelected);
    } else {
      setSelectedJobs([]);
    }
  };

  const handleJobClick = (id: string) => {
    const selectedIndex = selectedJobs.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      // Add to selection
      newSelected = [...selectedJobs, id];
    } else {
      // Remove from selection
      newSelected = selectedJobs.filter((jobId) => jobId !== id);
    }

    setSelectedJobs(newSelected);
  };

  const isSelected = (id: string) => selectedJobs.indexOf(id) !== -1;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header with batch info and back button */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          sx={{ mr: 1 }}
          onClick={() => {
            onClose();
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h6">{batch.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {batch.brand.name}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: "1 1 150px", minWidth: "150px" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by SKU"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">
                  All statuses ({batchJobs.length})
                </MenuItem>
                {Object.values(JobStatus).map((status) => {
                  // Count jobs with this status
                  const count = batchJobs.filter(
                    (job) => job.status === status
                  ).length;

                  return (
                    <MenuItem key={status} value={status}>
                      {statusToLabel(status)} ({count})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={selectedColumn}
                onChange={(e) => setSelectedColumn(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select column to group by</em>
                </MenuItem>
                {availableColumns.map((column) => {
                  // Special handling for garment_type to show a more user-friendly label
                  if (column === SPECIAL_COLUMNS.GARMENT_TYPE) {
                    return (
                      <MenuItem key={column} value={column}>
                        Garment Type (AI Generated)
                      </MenuItem>
                    );
                  }

                  // For sheet data columns, show a preview of the value
                  return (
                    <MenuItem key={column} value={column}>
                      {column} (
                      {sheet?.items[0]
                        ? String(sheet.items[0][column] || "").substring(0, 12)
                        : ""}
                      ... )
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{ flex: "1 1 200px", display: "flex", alignItems: "center" }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showUnassignedOnly}
                  onChange={(e) => setShowUnassignedOnly(e.target.checked)}
                />
              }
              label="Only unassigned"
            />
          </Box>
        </Box>
      </Paper>

      {filteredAndGroupedJobs.groups ? (
        <Box>
          <Typography variant="subtitle1" sx={{ my: 2 }}>
            Grouped by: <strong>{selectedColumn}</strong>
          </Typography>

          {Object.entries(filteredAndGroupedJobs.groups).map(
            ([groupName, groupJobs]) => (
              <Box key={groupName} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, display: "flex", alignItems: "center" }}
                >
                  {groupName}{" "}
                  <Chip label={groupJobs.length} size="small" sx={{ ml: 1 }} />
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: "48px" }}>
                          <Checkbox
                            indeterminate={
                              selectedJobs.filter((id) =>
                                groupJobs.some((job) => job.id === id)
                              ).length > 0 &&
                              selectedJobs.filter((id) =>
                                groupJobs.some((job) => job.id === id)
                              ).length < groupJobs.length
                            }
                            checked={
                              groupJobs.length > 0 &&
                              groupJobs.every((job) =>
                                selectedJobs.includes(job.id)
                              )
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all jobs in this group
                                const newSelected = [...selectedJobs];
                                groupJobs.forEach((job) => {
                                  if (!newSelected.includes(job.id)) {
                                    newSelected.push(job.id);
                                  }
                                });
                                setSelectedJobs(newSelected);
                              } else {
                                // Deselect all jobs in this group
                                setSelectedJobs(
                                  selectedJobs.filter(
                                    (id) =>
                                      !groupJobs.some((job) => job.id === id)
                                  )
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: "15%" }}>SKU</TableCell>
                        <TableCell sx={{ width: "25%" }}>Preview</TableCell>
                        <TableCell sx={{ width: "15%" }}>
                          Garment Type
                        </TableCell>
                        <TableCell sx={{ width: "15%" }}>Status</TableCell>
                        <TableCell sx={{ width: "20%" }}>Copywriter</TableCell>
                        <TableCell sx={{ width: "10%" }} align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupJobs.map((job) => (
                        <JobAuthorizationBatchItem
                          key={job.id}
                          job={job}
                          isSelected={isSelected(job.id)}
                          copywriters={copywriters}
                          onJobClick={handleJobClick}
                          onAssignButtonClick={(id) => {
                            setSingleJobAssignmentId(id);
                            setAssignmentDialogOpen(true);
                          }}
                          onUnassignButtonClick={async (id) => {
                            await deleteJobAuthorization([id]);
                            await fetchBatchJobs();
                          }}
                        />
                      ))}
                      {groupJobs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ py: 2 }}
                            >
                              No jobs found in this group
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
          )}
        </Box>
      ) : (
        // Display ungrouped jobs (original table)
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: "48px" }}>
                  <Checkbox
                    indeterminate={
                      selectedJobs.length > 0 &&
                      selectedJobs.length < filteredJobs.length
                    }
                    checked={
                      filteredJobs.length > 0 &&
                      selectedJobs.length === filteredJobs.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell sx={{ width: "15%" }}>SKU</TableCell>
                <TableCell sx={{ width: "25%" }}>Preview</TableCell>
                <TableCell sx={{ width: "15%" }}>Garment Type</TableCell>
                <TableCell sx={{ width: "15%" }}>Status</TableCell>
                <TableCell sx={{ width: "20%" }}>Copywriter</TableCell>
                <TableCell sx={{ width: "10%" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.map((job) => (
                <JobAuthorizationBatchItem
                  key={job.id}
                  job={job}
                  isSelected={isSelected(job.id)}
                  copywriters={copywriters}
                  onJobClick={handleJobClick}
                  onAssignButtonClick={(id) => {
                    setSingleJobAssignmentId(id);
                    setAssignmentDialogOpen(true);
                  }}
                  onUnassignButtonClick={async (id) => {
                    await deleteJobAuthorization([id]);
                    await fetchBatchJobs();
                  }}
                />
              ))}
              {filteredJobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      No jobs found with selected filters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedJobs.length > 0 && (
        <Paper
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            bottom: 16,
            mt: 2,
            boxShadow: 3,
            borderRadius: 1,
            width: "calc(100% - 32px)",
            marginLeft: "auto",
            marginRight: "auto",
            zIndex: 10,
          }}
          elevation={3}
        >
          <Typography variant="body2" fontWeight="medium">
            {selectedJobs.length}{" "}
            {selectedJobs.length === 1 ? "job selected" : "jobs selected"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Delete />}
              onClick={async () => {
                const authorizationIds = batchJobs
                  .filter((job) => selectedJobs.includes(job.id))
                  .map((job) =>
                    job.assignments.map((assignment) => assignment.id)
                  )
                  .flat();

                await deleteJobAuthorization(authorizationIds);
                await fetchBatchJobs();
                await setSelectedJobs([]);
              }}
              color="error"
            >
              Remove assignments
            </Button>

            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setAssignmentDialogOpen(true)}
              color="primary"
            >
              Assign copywriter
            </Button>
          </Box>
        </Paper>
      )}

      <JobAssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => {
          setAssignmentDialogOpen(false);
          setSingleJobAssignmentId(null);
        }}
        copywriters={copywriters}
        selectedJobs={
          singleJobAssignmentId
            ? batchJobs.filter((job) => job.id === singleJobAssignmentId)
            : batchJobs.filter((job) => selectedJobs.includes(job.id))
        }
        assignedJobs={batchJobs.filter((job) => job.is_assigned)}
        onAssignmentComplete={() => {
          // Refresh data after assignment
          fetchBatchJobs();
          // Clear selections
          setSelectedJobs([]);
          setSingleJobAssignmentId(null);
        }}
      />
    </Box>
  );
}
